import React, { useState } from "react";
import { get } from "lodash";
import PropTypes from "prop-types";
import { Field } from "redux-form";
import {
  MonthYearField,
  Select,
  FormInput,
  Check,
  CheckGroup,
  AutoCompleteUserInputField
} from "MODULES/shared/components/Forms";
import CurrencyField from "MODULES/shared/components/Forms/CurrencyField";
import {
  AS_IS,
  AWAITING_REPLY,
  EXCHANGE_ONLY,
  INCIDENT_RELATED,
  INCOMING_LINE_ITEMS,
  MAKE_OFFER,
  MODIFIED,
  STANDARD,
  RFQ_AS_ALT_ID
} from "MODULES/IncomingQuotes/constants";
import { arrayToOptions, toOptions } from "LIB/forms";
import FormRow from "MODULES/shared/components/Forms/FormRow";
import { Col, FormGroup } from "react-bootstrap";
import {
  CONDITION,
  LEAD_TIME,
  TAG_DATE,
  TAGGED_BY,
  TRACE_TYPE,
  TRACED_TO,
  WARRANTY_OPTIONS
} from "MODULES/inventories/constants";
import { properCase } from "LIB/formatters/text";
import { EXCHANGE_FEE, WARRANTY } from "MODULES/OutgoingQuotes/constants";
import { COST, QUANTITY } from "APP_ROOT/constants";
import api from "APP_ROOT/api";
import {
  ACTIVE,
  CREDIT_HOLD,
  DENIED,
  MANAGER_CONSENT_NEEDED,
  REPAIR_STATION_STATUS
} from "MODULES/companies/constants";
import Warning from "MODULES/shared/components/Warning/Warning";
import { EXPECTED_SERIAL_NUMBER } from "MODULES/PurchaseOrders/constants";

export default function PartDetails({ quoteType, part, taggedBy }) {
  const options = [
    { value: "overhauled", text: "Overhauled" },
    { value: "repaired", text: "Repaired" },
    { value: "inspected", text: "Inspected" },
    { value: "new_item", text: "New" },
    { value: "as_removed", text: "As Removed" },
    { value: "beyond_econ_repair", text: "Beyond Econ Repair" }
  ];

  const [selectedTaggedBy, setTaggedBy] = useState(taggedBy);

  const alternateOptions = alternateParts =>
    alternateParts
      .get("alternates")
      .toJS()
      .map(val => ({
        text: val.name,
        value: val.id
      }));

  const searchCompanies = filter =>
    api.companies
      .search({
        filter,
        status: [ACTIVE, CREDIT_HOLD].join(","),
        page: 1,
        limit: 20
      })
      .then(resp => resp.data.results);

  return (
    <div>
      <FormRow>
        <Col md={4}>
          <div className="form-group">
            <Field
              name={CONDITION}
              component={Select}
              options={options}
              label={properCase(CONDITION)}
              required
            />
          </div>
        </Col>
        <Col md={2}>
          <div className="form-group">
            <Field
              name={QUANTITY}
              component={FormInput}
              label="QTY"
              type="number"
              required
              inputProps={{
                min: 0,
                onKeyDown: e => {
                  if (["ArrowUp", "ArrowDown"].includes(e.key)) {
                    e.preventDefault();
                  }
                },
                onWheel: e => e.target.blur()
              }}
            />
          </div>
        </Col>
        <Col md={3}>
          <div className="form-group">
            <CurrencyField
              name={COST}
              label={properCase(COST)}
              required={
                quoteType !== AWAITING_REPLY && quoteType !== EXCHANGE_ONLY
              }
              readOnly={quoteType === MAKE_OFFER}
              disabled={
                quoteType === MAKE_OFFER || quoteType === AWAITING_REPLY
              }
            />
          </div>
        </Col>
        <Col md={3}>
          <div className="form-group">
            <CurrencyField
              name={EXCHANGE_FEE}
              label="Exch Fee"
              required={quoteType === EXCHANGE_ONLY}
              disabled={quoteType === AWAITING_REPLY}
            />
          </div>
        </Col>
        {/* col */}
      </FormRow>
      <FormRow>
        <Col md={3}>
          <div className="form-group">
            <Field
              name={LEAD_TIME}
              component={FormInput}
              label="Lead Time (Days)"
              type="number"
              required={[EXCHANGE_ONLY, STANDARD].includes(quoteType)}
              inputProps={{
                min: 0,
                onKeyDown: e => {
                  if (["ArrowUp", "ArrowDown"].includes(e.key)) {
                    e.preventDefault();
                  }
                },
                onWheel: e => e.target.blur()
              }}
            />
          </div>
        </Col>
        <Col md={3}>
          <div className="form-group">
            <Field
              name={EXPECTED_SERIAL_NUMBER}
              component={FormInput}
              label="Expected Serial #"
            />
          </div>
        </Col>
        {part && (
          <Col md={3}>
            <div className="form-group">
              <Field
                name={RFQ_AS_ALT_ID}
                label="RFQ as Alt"
                component={Select}
                options={alternateOptions(part)}
              />
            </div>
          </Col>
        )}
        <Col md={3}>
          <FormGroup>
            <Field
              component={Select}
              label={properCase(WARRANTY)}
              name={WARRANTY}
              options={arrayToOptions(WARRANTY_OPTIONS)}
            />
          </FormGroup>
        </Col>
      </FormRow>
      <hr className="hr-xs hr-dashed" style={{ marginBottom: 15 }} />
      <FormRow>
        <Col md={4}>
          <div className="form-group">
            <Field
              name={TRACE_TYPE}
              component={Select}
              options={toOptions(INCOMING_LINE_ITEMS.traceTypes)}
              label="Expected Trace Type"
            />
          </div>
        </Col>{" "}
        {/* col */}
        <Col md={4}>
          <div className="form-group">
            <Field
              name={TRACED_TO}
              component={FormInput}
              label="Expected Trace To"
            />
          </div>
        </Col>
        <Col md={2}>
          <CheckGroup title={properCase(INCIDENT_RELATED)}>
            <Field
              name={INCIDENT_RELATED}
              component={Check}
              label="IR &nbsp;&nbsp;&nbsp;&nbsp;"
            />
          </CheckGroup>
        </Col>
        <Col md={2}>
          <CheckGroup title="As Is">
            <Field name={AS_IS} component={Check} label="As-is" />
          </CheckGroup>
        </Col>
      </FormRow>
      <FormRow>
        <Col md={4}>
          <div className="form-group">
            <Field
              component={AutoCompleteUserInputField}
              formatItem={result => (result && result.name) || result}
              handleTaggedBy={item => setTaggedBy(item)}
              selectedItem={selectedTaggedBy}
              label={properCase(TAGGED_BY)}
              name={TAGGED_BY}
              placeholder="Search by a company's name"
              search={searchCompanies}
            />
          </div>
        </Col>
        <Col md={4}>
          <div className="form-group">
            <MonthYearField
              name={TAG_DATE}
              component={FormInput}
              label={properCase(TAG_DATE)}
            />
          </div>
        </Col>
        <Col md={4}>
          <div className="form-group">
            <MonthYearField name="dom" component={FormInput} label="DOM" />
          </div>
        </Col>
      </FormRow>
      {[MANAGER_CONSENT_NEEDED, DENIED].includes(
        get(selectedTaggedBy, REPAIR_STATION_STATUS)
      ) && (
        <Warning>
          The Repair Station has a status of{" "}
          {properCase(get(selectedTaggedBy, REPAIR_STATION_STATUS))}
        </Warning>
      )}
      <FormRow>
        <Col md={3}>
          <CheckGroup title={properCase(MODIFIED)}>
            <Field
              name={MODIFIED}
              component={Check}
              label={properCase(MODIFIED)}
            />
          </CheckGroup>
        </Col>
      </FormRow>
    </div>
  );
}

PartDetails.propTypes = {
  part: PropTypes.instanceOf(Map),
  quoteType: PropTypes.string,
  taggedBy: PropTypes.string
};



// WEBPACK FOOTER //
// ./app/modules/IncomingQuotes/PartDetails.jsx